import { useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import { format, parseISO, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { motion } from "framer-motion";
import { TrendingUp, BarChart3 } from "lucide-react";

const MOOD_VALUES = {
  very_happy: 5,
  happy: 4,
  neutral: 3,
  sad: 2,
  very_sad: 1,
  angry: 2,
  anxious: 2,
  tired: 2,
  in_love: 5,
  sick: 1,
  surprised: 4,
  relaxed: 4,
  sporty: 5,
  forgiven: 4,
};

const MOOD_COLORS = {
  very_happy: "#0aff60",
  happy: "#84cc16",
  neutral: "#ffbd00",
  sad: "#3b82f6",
  very_sad: "#8b5cf6",
  angry: "#ff003c",
  anxious: "#f97316",
  tired: "#64748b",
  in_love: "#ec4899",
  sick: "#10b981",
  surprised: "#f59e0b",
  relaxed: "#06b6d4",
  sporty: "#f97316",
  forgiven: "#8b5cf6",
};

const MOOD_LABELS = {
  very_happy: "Muy Feliz",
  happy: "Bien",
  neutral: "Neutral",
  sad: "Triste",
  very_sad: "Muy Triste",
  angry: "Enojado",
  anxious: "Ansioso",
  tired: "Cansado",
  in_love: "Enamorado",
  sick: "Enfermo",
  surprised: "Sorprendido",
  relaxed: "Relajado",
  sporty: "Deportista",
  forgiven: "Perdonado",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-void-paper border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="font-rajdhani font-semibold text-neon-cyan text-sm">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-slate-300 text-sm">
            {entry.name}: <span className="text-white font-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const MoodCharts = ({ moods, stats, loading }) => {
  // Prepare line chart data (last 30 days trend)
  const trendData = useMemo(() => {
    const last30Days = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = subDays(today, i);
      const dateStr = format(date, "yyyy-MM-dd");
      const mood = moods.find((m) => m.date === dateStr);
      
      last30Days.push({
        date: format(date, "dd/MM"),
        fullDate: format(date, "d MMM", { locale: es }),
        value: mood ? MOOD_VALUES[mood.mood] : null,
        mood: mood?.mood || null,
      });
    }
    
    return last30Days;
  }, [moods]);

  // Prepare bar chart data (mood frequency)
  const frequencyData = useMemo(() => {
    if (!stats?.mood_counts) return [];
    
    return Object.entries(stats.mood_counts)
      .map(([mood, count]) => ({
        mood,
        label: MOOD_LABELS[mood] || mood,
        count,
        color: MOOD_COLORS[mood] || "#64748b",
      }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (moods.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400 font-inter text-lg">
          No hay suficientes datos para mostrar estadísticas
        </p>
        <p className="text-slate-500 font-inter text-sm mt-2">
          Comienza a registrar tu estado de ánimo para ver gráficos
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Trend Line Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="chart-container"
      >
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-neon-cyan" />
          <h3 className="font-rajdhani font-bold text-lg uppercase tracking-wide text-slate-300">
            Tendencia de Ánimo (30 días)
          </h3>
        </div>
        
        <div className="h-[300px]" data-testid="mood-trend-chart">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="date" 
                stroke="#64748b"
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                tickLine={{ stroke: "#334155" }}
              />
              <YAxis 
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                stroke="#64748b"
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                tickLine={{ stroke: "#334155" }}
                tickFormatter={(value) => {
                  const labels = { 1: "😢", 2: "😔", 3: "😐", 4: "🙂", 5: "😄" };
                  return labels[value] || value;
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#00f3ff"
                strokeWidth={3}
                dot={{ fill: "#00f3ff", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 8, fill: "#00f3ff", stroke: "#bc13fe", strokeWidth: 2 }}
                connectNulls
                name="Nivel de ánimo"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex justify-center gap-6 mt-4 text-xs text-slate-500">
          <span>1 = Muy bajo</span>
          <span>3 = Neutral</span>
          <span>5 = Muy alto</span>
        </div>
      </motion.div>

      {/* Frequency Bar Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="chart-container"
      >
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-neon-purple" />
          <h3 className="font-rajdhani font-bold text-lg uppercase tracking-wide text-slate-300">
            Frecuencia de Estados de Ánimo
          </h3>
        </div>
        
        <div className="h-[300px]" data-testid="mood-frequency-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={frequencyData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="label" 
                stroke="#64748b"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={{ stroke: "#334155" }}
                angle={-45}
                textAnchor="end"
                interval={0}
              />
              <YAxis 
                stroke="#64748b"
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                tickLine={{ stroke: "#334155" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="count" 
                name="Veces registrado"
                radius={[4, 4, 0, 0]}
              >
                {frequencyData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Summary Stats */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="bg-black/30 rounded-xl p-4 border border-white/5 text-center">
            <p className="font-orbitron text-3xl text-neon-cyan">{stats.total_entries}</p>
            <p className="font-rajdhani text-sm text-slate-400 uppercase tracking-wide">Total Registros</p>
          </div>
          <div className="bg-black/30 rounded-xl p-4 border border-white/5 text-center">
            <p className="font-orbitron text-3xl text-neon-purple">{stats.average_mood.toFixed(1)}</p>
            <p className="font-rajdhani text-sm text-slate-400 uppercase tracking-wide">Promedio</p>
          </div>
          <div className="bg-black/30 rounded-xl p-4 border border-white/5 text-center">
            <p className="font-orbitron text-3xl text-neon-green">{stats.streak_days}</p>
            <p className="font-rajdhani text-sm text-slate-400 uppercase tracking-wide">Racha (días)</p>
          </div>
          <div className="bg-black/30 rounded-xl p-4 border border-white/5 text-center">
            <p className="text-3xl">{MOOD_LABELS[stats.most_common_mood] ? "👑" : "❓"}</p>
            <p className="font-rajdhani text-sm text-slate-400 uppercase tracking-wide">
              {MOOD_LABELS[stats.most_common_mood] || "N/A"}
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};
